import { useMutation, type UseMutationOptions, useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ApiClient } from './api.client.js';
import { type Router } from '../models/index.js';

type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };

export const RoutersApiKeys = {
  Routers: 'Routers',
};

export class RoutersApi extends ApiClient {
  public static getRouters(): Promise<WithRequired<Router, 'id'>[]> {
    return this.http.get(`/routers`).then((res) => res.data);
  }

  public static getRouter(routerId: string): Promise<Router> {
    return this.http.get(`/routers/${routerId}`).then((res) => res.data);
  }

  public static createRouter(routerDetails: Partial<Router>): Promise<void> {
    return this.http.post('/routers', routerDetails).then((res) => res.data);
  }

  public static updateRouter(routerDetails: Partial<Router & { id: string }>): Promise<void> {
    return this.http.put(`/routers/${routerDetails.id}`, routerDetails).then((res) => res.data);
  }

  public static deleteRouter(routerId: string): Promise<void> {
    return this.http.delete(`/routers/${routerId}`).then((res) => res.data);
  }
}

export function useAllRouters(queryOptions?: UseQueryOptions<WithRequired<Router, 'id'>[]>) {
  return useQuery<WithRequired<Router, 'id'>[]>([RoutersApiKeys.Routers], () => RoutersApi.getRouters(), queryOptions);
}

export function useOneRouter(routerId: string, queryOptions?: UseQueryOptions<Router>) {
  return useQuery<Router>([RoutersApiKeys.Routers, routerId], () => RoutersApi.getRouter(routerId), queryOptions);
}

export function useCreateRouter(mutationOptions?: UseMutationOptions<void, unknown, Partial<Router>>) {
  return useMutation<void, unknown, Partial<Router>>(
    [RoutersApiKeys.Routers],
    (routerDetails: Partial<Router>) => RoutersApi.createRouter(routerDetails),
    mutationOptions,
  );
}

export function useUpdateRouter(mutationOptions?: UseMutationOptions<void, unknown, Partial<Router>>) {
  return useMutation<void, unknown, Partial<Router>>(
    [RoutersApiKeys.Routers],
    (routerDetails: Partial<Router>) => RoutersApi.updateRouter(routerDetails),
    mutationOptions,
  );
}

export function useDeleteRouter(mutationOptions?: UseMutationOptions<void, unknown, string>) {
  return useMutation<void, unknown, string>(
    [RoutersApiKeys.Routers],
    (routerId: string) => RoutersApi.deleteRouter(routerId),
    mutationOptions,
  );
}
